/**
 * Field Registry — maps hosted asset field keys to custom Zod schemas, lucide icons,
 * input types, and component hints.
 *
 * Fields NOT listed here fall back to generic rendering based on their schemaType
 * (enum → Select, multi-enum → CheckboxGroup, text → Textarea, long → number Input, boolean → Checkbox).
 *
 * To add custom validation or a custom icon for a hosted user field, add an entry here
 * keyed by the field's `key` value from user-fields.json / configUser.js.
 */
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  Building2,
  MapPin,
  Globe,
  Hash,
  FileText,
  Phone,
  User,
  Youtube,
  type LucideIcon,
} from 'lucide-react';

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export interface FieldRegistryEntry {
  /** Zod schema override. When provided, replaces the auto-generated schema for this field. */
  zodSchema?: z.ZodTypeAny;
  /** Lucide icon component to show next to the field label. */
  icon?: LucideIcon;
  /** HTML input type override (e.g. 'tel', 'url', 'number'). */
  inputType?: string;
  /** Placeholder text override. */
  placeholder?: string;
  /** Override the schemaType rendering (e.g. force 'text' field to render as a single-line input, or 'address' for mapbox autocomplete, or 'city' for city-only autocomplete). */
  renderAs?: 'input' | 'textarea' | 'address' | 'phone' | 'city';
  /** Custom component key — reserved for future use. */
  component?: string;
  /** Group key for multi-step signup. Fields sharing the same group appear on the same wizard step. */
  signupStepGroup?: string;
  /** Tooltip text shown via an info icon next to the field label. */
  tooltip?: string;
}

/**
 * Ordered list of signup step groups. Fields are grouped by their `signupStepGroup`
 * registry value and presented in this order. Fields without a group go to 'default'.
 */
export const SIGNUP_STEP_GROUP_ORDER = ['contact', 'business', 'details'];

// ────────────────────────────────────────────
// Registry — client-specific field overrides
// ────────────────────────────────────────────

const fieldRegistry: Record<string, FieldRegistryEntry> = {
  // Provider fields
  business_name: {
    icon: Building2,
    placeholder: 'Your business name',
    renderAs: 'input',
    signupStepGroup: 'business',
    tooltip: 'The registered name of your business. This will appear on your public profile.',
  },

  street_provider: {
    zodSchema: z.object({
      address1: z.string(),
      address2: z.string(),
      formattedAddress: z.string().min(1, 'Address is required'),
      city: z.string(),
      region: z.string(),
      postalCode: z.string(),
      country: z.string(),
      lat: z.number(),
      lng: z.number(),
    }),
    icon: MapPin,
    placeholder: 'Search for your address...',
    renderAs: 'address',
    signupStepGroup: 'details',
  },

  orgname: {
    icon: Building2,
    placeholder: 'Organization name',
    renderAs: 'input',
    signupStepGroup: 'business',
    tooltip: 'The name of your organization or parent company, if different from your business name.',
  },

  businesswebsite_id: {
    zodSchema: z
      .string()
      .min(1, 'Website is required')
      .url('Please enter a valid URL (e.g. https://example.com)'),
    icon: Globe,
    inputType: 'url',
    placeholder: 'https://www.example.com',
    renderAs: 'input',
    signupStepGroup: 'business',
  },

  business_ein_id: {
    zodSchema: z
      .string()
      .min(1, 'EIN is required')
      .transform(val => val.replace(/\D/g, '')) // strip non-digits
      .refine(val => val.length === 9, { message: 'EIN must be 9 digits (XX-XXXXXXX)' })
      .refine(
        val => {
          // First two digits must be a valid IRS campus prefix
          const prefix = parseInt(val.slice(0, 2), 10);
          const validPrefixes = [
            ...Array.from({ length: 6 }, (_, i) => i + 1),    // 01-06
            ...Array.from({ length: 7 }, (_, i) => i + 10),   // 10-16
            ...Array.from({ length: 8 }, (_, i) => i + 20),   // 20-27
            ...Array.from({ length: 9 }, (_, i) => i + 30),   // 30-38
            ...Array.from({ length: 9 }, (_, i) => i + 40),   // 40-48
            ...Array.from({ length: 19 }, (_, i) => i + 50),  // 50-68
            ...Array.from({ length: 7 }, (_, i) => i + 71),   // 71-77
            ...Array.from({ length: 9 }, (_, i) => i + 80),   // 80-88
            ...Array.from({ length: 10 }, (_, i) => i + 90),  // 90-99
          ];
          return validPrefixes.includes(prefix);
        },
        { message: 'Please enter a valid EIN' }
      ),
    icon: Hash,
    placeholder: 'e.g. 12-3456789',
    renderAs: 'input',
    signupStepGroup: 'details',
  },

  // Customer / all user types fields
  usercity_id: {
    icon: MapPin,
    placeholder: 'Search for your city...',
    renderAs: 'city',
    signupStepGroup: 'contact',
  },

  userzipcode_id: {
    zodSchema: z
      .string()
      .refine(val => !val || /^\d{5}(-\d{4})?$/.test(val), {
        message: 'Please enter a valid ZIP code (e.g. 90210)',
      }),
    icon: MapPin,
    placeholder: 'e.g. 90210',
    renderAs: 'input',
    signupStepGroup: 'contact',
  },

  // Built-in user type fields (displayName, phoneNumber)
  displayName: {
    icon: User,
    placeholder: 'Your business or display name',
    renderAs: 'input',
    signupStepGroup: 'contact',
    tooltip: 'This is the name publicly visible on the marketplace for your organization.',
  },

  phoneNumber: {
    zodSchema: z
      .string()
      .min(1, 'Phone number is required')
      .refine(val => isValidPhoneNumber(val, 'US'), {
        message: 'Please enter a valid phone number',
      }),
    icon: Phone,
    renderAs: 'phone',
    signupStepGroup: 'contact',
  },

  // Generic overrides for common field patterns
  phone: {
    zodSchema: z
      .string()
      .min(1, 'Phone number is required')
      .refine(val => isValidPhoneNumber(val, 'US'), {
        message: 'Please enter a valid phone number',
      }),
    icon: Phone,
    renderAs: 'phone',
  },

  youtubeVideoUrl: {
    zodSchema: z
      .string()
      .url('Please enter a valid URL')
      .regex(
        /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//,
        'Please enter a valid YouTube URL'
      ),
    icon: Youtube,
    inputType: 'url',
    placeholder: 'https://youtube.com/watch?v=...',
  },
};

// ────────────────────────────────────────────
// Lookup
// ────────────────────────────────────────────

/**
 * Look up a field in the registry by its key.
 * Returns undefined if no custom entry exists (caller should fall back to generic rendering).
 */
export const getFieldRegistryEntry = (key: string): FieldRegistryEntry | undefined => {
  return fieldRegistry[key];
};

export default fieldRegistry;
